<template>
  <div>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-4">
          Security
        </h3>
        <p class="text--secondary">
          Manage your password and security settings.
        </p>
      </v-col>
    </v-row>

    <!-- Password Section -->
    <v-card
      v-if="hasPasswordProvider"
      outlined
      class="mb-4"
    >
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-3">
          <font-awesome-icon
            :icon="['fas', 'key']"
            class="mr-2"
          />
          Password
        </div>
        <p class="text-body-2 text--secondary mb-3">
          Change your account password. You will need to sign in again after updating.
        </p>
        <v-btn
          color="primary"
          outlined
          class="text-none"
          @click="promptToUpdatePassword"
        >
          <font-awesome-icon
            :icon="['fas', 'lock']"
            size="sm"
            class="mr-2"
          />
          Change Password
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Session Information -->
    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-3">
          <font-awesome-icon
            :icon="['fas', 'clock-rotate-left']"
            class="mr-2"
          />
          Session Information
        </div>

        <v-row class="my-1">
          <v-col
            cols="5"
            class="text-body-2 font-weight-medium"
          >
            Email verified
          </v-col>
          <v-col class="text-body-2">
            <v-chip
              :color="emailVerified ? 'success' : 'warning'"
              text-color="white"
              x-small
            >
              {{ emailVerified ? 'Verified' : 'Not verified' }}
            </v-chip>
          </v-col>
        </v-row>

        <v-row class="my-1">
          <v-col
            cols="5"
            class="text-body-2 font-weight-medium"
          >
            Last sign-in
          </v-col>
          <v-col class="text-body-2 text--secondary">
            {{ lastSignIn || 'Unknown' }}
          </v-col>
        </v-row>

        <v-row class="my-1">
          <v-col
            cols="5"
            class="text-body-2 font-weight-medium"
          >
            Account status
          </v-col>
          <v-col class="text-body-2">
            <v-chip
              :color="isUserActive ? 'success' : 'error'"
              text-color="white"
              x-small
            >
              {{ isUserActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Update Password Dialog -->
    <v-dialog
      v-model="showPasswordDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          <span class="text-h6 font-weight-bold">Change Password</span>
          <v-spacer />
          <v-btn
            icon
            @click="cancelUpdatePassword"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form
            ref="passwordForm"
            v-model="validPassword"
          >
            <v-text-field
              v-model="inputPassword"
              label="New Password"
              type="password"
              outlined
              :rules="passwordRules"
              required
              @keydown.enter="updateAuthPassword"
            />
          </v-form>

          <v-alert
            v-if="errorUpdatingPassword"
            type="error"
            dense
            class="mt-3"
          >
            {{ errorUpdatingPassword }}
          </v-alert>

          <v-alert
            v-if="successUpdatingPassword"
            type="success"
            dense
            class="mt-3"
          >
            Password updated successfully!
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="cancelUpdatePassword"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!validPassword || !inputPassword"
            :loading="updatingPassword"
            @click="updateAuthPassword"
          >
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import dates from '@/utils/dates'

const { mapGetters: mapAuthGetters, mapActions: mapAuthActions, mapState: mapAuthState } = createNamespacedHelpers('auth')

export default {
  name: 'SettingsSecurity',
  props: {
    security: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      domainDoc: null,
      showPasswordDialog: false,
      inputPassword: '',
      validPassword: false,
      updatingPassword: false,
      errorUpdatingPassword: '',
      successUpdatingPassword: false,
      passwordRules: [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 8) || 'Password must be at least 8 characters',
        (v) => /(?=.*[a-z])/.test(v) || 'Must contain a lowercase letter',
        (v) => /(?=.*[A-Z])/.test(v) || 'Must contain an uppercase letter',
        (v) => /(?=.*\d)/.test(v) || 'Must contain a number',
      ],
    }
  },
  computed: {
    ...mapAuthGetters(['currentUser']),
    ...mapAuthState(['user']),

    hasPasswordProvider() {
      if (!this.domainDoc) return false
      const providers = this.domainDoc.providers || []
      return providers.some((p) => p.providerId === 'password' && p.isActive)
    },

    emailVerified() {
      return this.currentUser?.emailVerified || false
    },

    lastSignIn() {
      const timestamp = this.currentUser?.lastSignInTimestamp
        || this.currentUser?.lastSignInServerTimestamp
      if (!timestamp) return null
      try {
        return dates.getFormattedLocalDateTime(timestamp)
      } catch {
        return timestamp
      }
    },

    isUserActive() {
      return this.currentUser?.isUserActive || this.currentUser?.isActive || false
    },

    userEmail() {
      return this.user?.email || ''
    },
  },
  async created() {
    await this.loadDomainDoc()
  },
  methods: {
    ...mapAuthActions(['updatePassword']),

    async loadDomainDoc() {
      try {
        const domainName = this.userEmail.split('@')[1]
        if (!domainName) return
        this.domainDoc = await this.$store.dispatch('auth/getDomainDocFromFirestore', domainName)
      } catch {
        this.domainDoc = null
      }
    },

    promptToUpdatePassword() {
      this.showPasswordDialog = true
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false
      this.inputPassword = ''
    },

    cancelUpdatePassword() {
      this.showPasswordDialog = false
      this.inputPassword = ''
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false
    },

    async updateAuthPassword() {
      this.updatingPassword = true
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false

      try {
        const domainDoc = this.domainDoc
          || await this.$store.dispatch('auth/getDomainDocFromFirestore', this.userEmail.split('@')[1])

        if (!domainDoc || domainDoc.error) {
          this.errorUpdatingPassword = 'Unable to verify your domain. Please try again.'
          return
        }

        if (!domainDoc.isActive) {
          this.errorUpdatingPassword = 'Your domain is not active.'
          return
        }

        const updateData = {
          email: this.userEmail,
          password: this.inputPassword,
          tenantId: domainDoc.tenantId,
          url: window.location.href,
        }

        await this.updatePassword(updateData)
        this.successUpdatingPassword = true
        this.inputPassword = ''
      } catch (error) {
        this.errorUpdatingPassword = error?.message || 'Failed to update password'
      } finally {
        this.updatingPassword = false
      }
    },
  },
}
</script>
